import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/max';

const countries = new Set(getCountries());
const countryCode = (value) => countries.has(value) ? value : undefined;
const invalidPhone = 'Введите корректный номер телефона';
const hasControlCharacters = (value) => [...value].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);

// Parse the whole field. A country is an explicit user choice, never an IP/CRM-region default.
export const parseContactPhone = (value, country) => {
  const raw = String(value ?? '');
  const input = raw.trim();
  const result = { valid: false, empty: !input, number: '', country: '', callingCode: '', formatted: input, error: '' };
  if (hasControlCharacters(raw) || input.length > 64 || (input && !/^\+?[\d ()-]+$/.test(input))) {
    return { ...result, error: invalidPhone };
  }
  if (!input) return { ...result, valid: true };
  if (!input.startsWith('+') && !countryCode(country)) return { ...result, error: 'Выберите страну номера телефона' };
  const phone = parsePhoneNumberFromString(input, { defaultCountry: countryCode(country), extract: false });
  if (!phone) return { ...result, error: invalidPhone };
  const valid = phone.isValid() && !phone.ext
    && (!input.startsWith('+') || input.replace(/\D/g, '') === phone.number.slice(1));
  return {
    ...result,
    valid,
    number: valid ? phone.number : '',
    country: phone.country || '',
    callingCode: phone.countryCallingCode,
    formatted: phone.formatInternational(),
    error: valid ? '' : invalidPhone,
  };
};

// During editing even an incomplete/too-long phone retains every entered digit.
export const formatContactPhone = (value, country) => {
  const raw = String(value ?? '');
  const input = raw.trim();
  if (hasControlCharacters(raw) || !/^\+?[\d ()-]+$/.test(input)
      || (!input.startsWith('+') && !countryCode(country))) return raw;
  const formatted = new AsYouType(countryCode(country)).input(input);
  return formatted.replace(/\D/g, '') === input.replace(/\D/g, '') ? formatted : raw;
};

export const isValidContactPhone = (value, country) => parseContactPhone(value, country).valid;

export const normalizeContactPhone = (value, country) => {
  const parsed = parseContactPhone(value, country);
  if (!parsed.valid) throw new Error(parsed.error);
  return parsed.number;
};

export const displayContactPhone = (value) => {
  const parsed = parseContactPhone(value);
  return parsed.valid ? parsed.formatted : String(value ?? '');
};

export const contactPhoneHref = (value) => {
  const parsed = parseContactPhone(value);
  return parsed.valid && !parsed.empty ? `tel:${parsed.number}` : '';
};

const optionsByLocale = new Map();
export const getPhoneCountryOptions = (locale = 'ru') => {
  if (!optionsByLocale.has(locale)) {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    optionsByLocale.set(locale, [...countries].map((value) => ({
      value, callingCode: getCountryCallingCode(value), label: `${names.of(value)} (+${getCountryCallingCode(value)})`,
    })).sort((a, b) => a.label.localeCompare(b.label, locale)));
  }
  return optionsByLocale.get(locale);
};
